import { useCallback, useEffect, useState } from 'react'
import { Alert, Box, Button, Chip, CircularProgress, Paper, Stack, Typography } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { listarAlertas, marcarVisto, verificarAgora, type Alerta } from '../api/alertas'
import { AlertasPorEmail } from '../components/AlertasPorEmail'
import { useAuth } from '../state/AuthContext'
import { useFiltros } from '../state/FiltrosContext'

const ROTA = { trafego_pago: '/trafego-pago', organico: '/organico', leads: '/leads' } as const
const br = (iso: string) => iso.split('-').reverse().join('/')
const DIA_SEMANA = ['domingo', 'segunda', 'terça', 'quarta', 'quinta', 'sexta', 'sábado']
const diaDaSemana = (iso: string) => DIA_SEMANA[new Date(`${iso}T12:00:00Z`).getUTCDay()]

/**
 * Alertas dos últimos 30 dias: o dia de ontem de cada marca contra os 14 anteriores, e a
 * conferência de rastreamento da semana. Gerados todo dia às 6h30, junto com a coleta diária.
 */
export function AlertasPage() {
  const { pode } = useAuth()
  const { marcaId, setMarcaId } = useFiltros()
  const navigate = useNavigate()
  const [alertas, setAlertas] = useState<Alerta[] | null>(null)
  const [erro, setErro] = useState<string | null>(null)
  const [verificando, setVerificando] = useState(false)
  const [soCriticos, setSoCriticos] = useState(false)

  const carregar = useCallback(async () => {
    try {
      setAlertas(await listarAlertas(marcaId || null))
      marcarVisto()
    } catch (e) {
      setErro((e as Error).message)
    }
  }, [marcaId])

  useEffect(() => {
    setAlertas(null)
    carregar()
  }, [carregar])

  const verificar = async () => {
    setVerificando(true)
    setErro(null)
    try {
      await verificarAgora(marcaId || null)
      await carregar()
    } catch (e) {
      setErro((e as Error).message)
    } finally {
      setVerificando(false)
    }
  }

  const lista = (alertas ?? []).filter((a) => !soCriticos || a.severidade === 'critico')
  const porDia = [...new Set(lista.map((a) => a.data))].map((d) => [d, lista.filter((a) => a.data === d)] as const)

  return (
    <Stack spacing={2}>
      <Stack direction="row" alignItems="flex-start" spacing={2} useFlexGap flexWrap="wrap">
        <Box sx={{ mr: 'auto', maxWidth: 760 }}>
          <Typography variant="h5" sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700, lineHeight: 1.2 }}>
            Alertas
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Todo dia às 6h30 o sistema compara o dia de ontem de cada marca com os 14 dias anteriores — gasto, conversões, custo, cliques,
            visitas ao site e leads — e, aos domingos, confere se as conversões das plataformas batem com os leads que chegaram do site. Só
            entram marcas com volume suficiente para a comparação fazer sentido.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Chip label="Só críticos" color={soCriticos ? 'error' : 'default'} variant={soCriticos ? 'filled' : 'outlined'} onClick={() => setSoCriticos((v) => !v)} />
          {pode('marcas.gerenciar') ? (
            <Button size="small" variant="outlined" disabled={verificando} onClick={verificar} sx={{ textTransform: 'none' }}>
              {verificando ? 'Verificando…' : 'Verificar agora'}
            </Button>
          ) : null}
        </Stack>
      </Stack>

      <AlertasPorEmail />

      {erro ? (
        <Alert severity="error" onClose={() => setErro(null)}>
          {erro}
        </Alert>
      ) : null}

      {!alertas ? (
        <Box sx={{ display: 'grid', placeItems: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : !porDia.length ? (
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography color="text.secondary">
            Nenhum alerta nos últimos 30 dias{soCriticos ? ' entre os críticos' : ''}. Os alertas são gerados junto com a coleta diária (Configurações →
            Marcas): sem ela ligada, use “Verificar agora”.
          </Typography>
        </Paper>
      ) : (
        porDia.map(([dia, itens]) => (
          <Stack key={dia} spacing={1}>
            <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: 0.6 }}>
              {diaDaSemana(dia)}, {br(dia)}
            </Typography>
            {itens.map((a) => (
              <Paper key={a.id} variant="outlined" sx={{ p: 2, borderLeft: 4, borderLeftColor: a.severidade === 'critico' ? 'error.main' : 'warning.main' }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'flex-start' }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap" sx={{ mb: 0.5 }}>
                      <Chip size="small" variant="outlined" color={a.severidade === 'critico' ? 'error' : 'warning'} label={a.severidade === 'critico' ? 'Crítico' : 'Atenção'} />
                      <Typography variant="caption" color="text.secondary">
                        {a.marca}
                      </Typography>
                    </Stack>
                    <Typography variant="subtitle2">{a.titulo}</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                      {a.texto}
                    </Typography>
                  </Box>
                  {a.painel ? (
                    <Button
                      size="small"
                      onClick={() => {
                        setMarcaId(a.marcaId)
                        navigate(ROTA[a.painel!])
                      }}
                      sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
                    >
                      Abrir o painel
                    </Button>
                  ) : null}
                </Stack>
              </Paper>
            ))}
          </Stack>
        ))
      )}
    </Stack>
  )
}
